/**
 * Der HTTP-Transport — die **eine** Stelle, an der dieser Client mit `/api/v1/...` redet.
 *
 * Alles, was eine Entscheidung ist, liegt hier und nicht in den Komponenten: welcher Header die
 * Berechtigung trägt, wie eine Antwort geprüft wird, was ein Fehler ist. Eine Komponente, die
 * selbst `fetch` aufruft, wäre die zweite Stelle, an der ein Token in eine Adresse geraten oder
 * eine Prüfung ausbleiben könnte.
 *
 * **Relative Pfade, keine Basis-URL.** In Produktion serviert Fastify das gebaute Frontend
 * selbst (`CLAUDE.md`, Deployment), in der Entwicklung leitet Vite `/api` weiter. Eine
 * Basis-URL wäre eine Einstellung, die in einer der beiden Umgebungen falsch ist — und zwar
 * erst nach dem Deployment auffällig.
 *
 * **Die Antwort wird gegen ein zod-Schema geprüft** (Regel 7: in beide Richtungen validiert).
 * Was nicht passt, ist ein Fehler und kein Wert. Ohne das wanderte eine Formabweichung als
 * `undefined` durch die Oberfläche und fiele an einer Stelle auf, die nichts damit zu tun hat.
 *
 * **Ergebnis statt Ausnahme.** Ein Fehlschlag ist hier der Normalfall — eine volle Lobby, ein
 * abgelaufener Link, ein Server im Neustart. Als Ausnahme müsste ihn jede Aufrufstelle fangen,
 * und eine vergessene Stelle wäre ein weißer Bildschirm. Als Wert ist er sichtbar und muss
 * behandelt werden.
 *
 * **Der Code ist die Schnittstelle, nicht der Text** — dieselbe Zusage wie serverseitig
 * (`apps/server/src/routes/errors.ts`). Die Oberfläche entscheidet am Code und übersetzt über
 * i18n; der Text des Servers ist eine Rückfallebene für Fälle, die noch keine Übersetzung haben.
 *
 * **Fehlermeldungen tragen keinen Token.** Sie landen auf dem Bildschirm und in Fehlerberichten;
 * ein Token darin wäre dieselbe Preisgabe wie ein Token in einem Log. Deshalb wird die Meldung
 * einer fehlgeschlagenen Verbindung **nicht** durchgereicht, sondern ersetzt — der Text von
 * `fetch` kann die Adresse und damit mitgegebene Werte enthalten.
 */

import { apiErrorSchema, type ApiErrorCode } from '@flaghex/contracts';
import type { ZodType } from 'zod';

/**
 * Die Fehlerursachen, die dieser Client kennt: die des Servers plus zwei eigene.
 *
 * - `NETWORK` — der Server war nicht erreichbar. Für einen Testspieler etwas völlig anderes als
 *   „nicht berechtigt", und beides als dasselbe anzuzeigen würde jede Fehlersuche verlängern.
 * - `MALFORMED` — die Antwort passte nicht auf das erwartete Schema. Das ist ein Hinweis auf
 *   **Auseinanderlaufen** zwischen Client und Server und soll deshalb sichtbar sein, statt als
 *   `INTERNAL` unter den Serverfehlern zu verschwinden.
 */
export type FailureCode = ApiErrorCode | 'NETWORK' | 'MALFORMED';

export interface ApiFailure {
  readonly kind: 'failure';
  readonly code: FailureCode;
  /** Für Menschen. Geht durch i18n, sobald die Oberfläche den Code kennt (Regel 6). */
  readonly message: string;
  /** Der HTTP-Status, wenn es überhaupt eine Antwort gab. */
  readonly status?: number;
}

export type ApiResult<T> = { readonly kind: 'ok'; readonly value: T } | ApiFailure;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface RequestSpec<T> {
  readonly method: HttpMethod;
  /** Relativ, mit führendem Schrägstrich: `/api/v1/...`. */
  readonly path: string;
  /** Die erwartete Form der Erfolgsantwort. Für 204 ist das `z.null()`. */
  readonly schema: ZodType<T>;
  /** Die Berechtigung — **immer** als Header, nie in der Adresse. */
  readonly token?: { readonly header: string; readonly value: string };
  readonly body?: unknown;
}

export interface TransportOptions {
  /** Hereingegeben, damit Tests ohne Netz auskommen. */
  readonly fetch?: typeof fetch;
}

export type Transport = <T>(spec: RequestSpec<T>) => Promise<ApiResult<T>>;

function failure(code: FailureCode, message: string, status?: number): ApiFailure {
  return { kind: 'failure', code, message, ...(status === undefined ? {} : { status }) };
}

/**
 * Liest den Rumpf als JSON — oder `undefined`.
 *
 * Ein leerer Rumpf ist der Normalfall (204), kein Fehler: vier Hostbefehle antworten so. Ohne
 * diese Unterscheidung wäre jeder davon ein Fehlschlag, weil `json()` auf leerem Rumpf wirft.
 */
async function bodyOf(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return null;
  }
  try {
    return await response.json();
  } catch {
    return undefined;
  }
}

export function createTransport(options: TransportOptions = {}): Transport {
  const call = options.fetch ?? ((...args: Parameters<typeof fetch>) => fetch(...args));

  return async <T>(spec: RequestSpec<T>): Promise<ApiResult<T>> => {
    const headers: Record<string, string> = {};
    if (spec.token !== undefined) {
      headers[spec.token.header] = spec.token.value;
    }
    if (spec.body !== undefined) {
      headers['content-type'] = 'application/json';
    }

    let response: Response;
    try {
      response = await call(spec.path, {
        method: spec.method,
        headers,
        ...(spec.body === undefined ? {} : { body: JSON.stringify(spec.body) }),
      });
    } catch {
      // Die Meldung von `fetch` wird **nicht** durchgereicht: sie kann die Adresse und damit
      // mitgegebene Werte enthalten. Siehe Modulkopf.
      return failure('NETWORK', 'Der Dienst ist nicht erreichbar');
    }

    const body = await bodyOf(response);

    if (!response.ok) {
      const parsed = apiErrorSchema.safeParse(body);
      if (!parsed.success) {
        // Keinen Code aus dem Status erfinden: das wäre eine zweite Wahrheit über die
        // Fehlertabelle des Servers.
        return failure('MALFORMED', 'Der Dienst hat unerwartet geantwortet', response.status);
      }
      return failure(parsed.data.code, parsed.data.message, response.status);
    }

    const parsed = spec.schema.safeParse(body);
    if (!parsed.success) {
      // Ohne zod-Pfad: er beschreibt die innere Form des Protokolls und nützt dem Spieler
      // nichts — dieselbe Entscheidung wie bei `INVALID_MESSAGE` im WebSocket-Dispatch.
      return failure('MALFORMED', 'Die Antwort des Dienstes war unlesbar', response.status);
    }
    return { kind: 'ok', value: parsed.data };
  };
}
